import React, { useState, useEffect, useRef } from 'react';
import ProjectChat from './ProjectChat';
import IterationHistory from './IterationHistory';
import CodeEditor from './CodeEditor';
import LogTab from './LogTab';
import AgentProgressBar from './AgentProgressBar';
import GitHubImport from './GitHubImport';
import GitPushModal from './GitPushModal';
import ShareModal from './workspace/ShareModal';
import TemplateFilePreview from './TemplateFilePreview';
import MobilePreview from './MobilePreview';
import { hasTemplateFiles } from '../templates';
import { seedProjectTemplate } from '../utils/seedProjectTemplate';
import { authFetch, listJobs, getSecurityFindings } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useTheme } from '../hooks/useTheme';
import useWebSocket from '../hooks/useWebSocket';
import useEditorHistory from '../hooks/useEditorHistory';
import { WorkspaceSkeleton } from './Skeleton';
import DomainsTab from './DomainsTab';
import CollabBar from './collab/CollabBar';
import InviteDialog from './collab/InviteDialog';
import ActivityFeed from './collab/ActivityFeed';
import AIDiffReview from './collab/AIDiffReview';
import ReconnectBanner from './collab/ReconnectBanner';
import useCollabSocket from '../hooks/useCollabSocket';
import previewTabIcon from '../preview.png';
import codeTabIcon from '../code.png';
import historyTabIcon from '../history.png';
import liveLogTabIcon from '../file.png';
import settingsIcon from '../setting (1).png';
import downloadIcon from '../business (1).png';
import shareIcon from '../share (2).png';
import gitPushIcon from '../branch (1).png';

const PANEL_TABS = [
  { id: 'preview', label: 'Preview', icon: previewTabIcon },
  { id: 'code', label: 'Code', icon: codeTabIcon },
  { id: 'history', label: 'History', icon: historyTabIcon },
  { id: 'log', label: 'Live Log', icon: liveLogTabIcon },
  { id: 'domains', label: 'Domains', icon: null },
];

export default function ProjectWorkspace({
  projectId, onBack, initialJobId, templateMeta, onOpenSettings,
  initialPrompt = null, initialAction = null, onInitialConsumed,
}) {
  const { mode, toggle } = useTheme();
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState({});
  const [activeTab, setActiveTab] = useState('preview');
  const [previewFiles, setPreviewFiles] = useState(null);
  const [rightPanel, setRightPanel] = useState('chat');
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(!!initialJobId);
  const [currentJobId, setCurrentJobId] = useState(initialJobId || null);
  const [showImport, setShowImport] = useState(false);
  const [showPush, setShowPush] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [agentPhase, setAgentPhase] = useState('idle');
  // workspaceVisible: flips true the moment generation starts (or files already exist)
  // Never flips back — once the workspace is shown it stays shown
  // ONE-SCREEN-CREATE: the hero screen is gone. The prompt is written
  // on the New Project screen, so the workspace opens straight onto
  // code + preview; a project created with no prompt lands here too and
  // uses the Project Chat composer on the right (see EmptyPreviewState).
  const [workspaceVisible] = useState(true);
  const chatImportSuccessRef = React.useRef(null);
  const chatRef = React.useRef(null);

  // ── Collaboration ─────────────────────────────────────────────────────────
  const collabEnabled = process.env.REACT_APP_COLLAB_ENABLED !== 'false';
  const collab = useCollabSocket(projectId, { enabled: collabEnabled });
  const [showInvite, setShowInvite] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [liveActivityEvents, setLiveActivityEvents] = useState([]);

  // Append collab activity events to feed
  React.useEffect(() => {
    if (!collab.members) return;
  }, [collab.members]);

  // Reload files when a collaborator applies AI changes or file ops
  React.useEffect(() => {
    if (collab.fileOps) fetchProject();
  }, [collab.fileOps]);

  collab.onAiApplied(() => fetchProject());
  // ─────────────────────────────────────────────────────────────────────────
  const templateSeededRef = useRef(false);
  const toast = useToast();
  const history = useEditorHistory(projectId);

  const {
    logs: liveLogs,
    isConnected: logsConnected,
    reconnectAttempt: logsReconnectAttempt,
    reconnectCountdown: logsReconnectCountdown,
    fatalError: logsFatalError,
  } = useWebSocket(currentJobId, { enabled: Boolean(currentJobId) });

  // Runtime error logs from Sandpack (merged with WebSocket logs)
  const [runtimeLogs, setRuntimeLogs] = useState([]);
  const runtimeLogIdRef = useRef(0);

  const handleRuntimeError = React.useCallback((error) => {
    setRuntimeLogs(prev => {
      const entry = {
        id: `rt-${++runtimeLogIdRef.current}`,
        time: new Date().toLocaleTimeString(),
        event: 'runtime_error',
        detail: `${error.message || error}${error.file ? ` (${error.file}${error.line ? `:${error.line}` : ''})` : ''}`,
        type: 'error',
        phase: 'Deploying',
        agentType: 'RUNTIME',
      };
      const next = [...prev, entry];
      return next.length > 50 ? next.slice(-50) : next;
    });
  }, []);

  // Deployment logs pushed directly from handlePublish (not via WebSocket)
  const [deployLogs, setDeployLogs] = useState([]);
  const deployLogIdRef = useRef(0);

  // SECURITY-SCAN-PREVIEW-WARNING-FIX: the security scan (semgrep/bandit)
  // used to only ever surface its result reactively, inside GitPushModal,
  // at the moment a user tried to push to git — someone who just opened
  // the preview and started using/sharing the app had no idea HIGH/
  // CRITICAL findings existed. The scan already runs in the background and
  // publishes a `security_scan_completed` event with `blocked`/`summary`
  // (core/orchestrator.py's run_background_quality) — this surfaces that
  // same event as soon as it arrives, regardless of which tab is open,
  // instead of waiting for a push/deploy attempt.
  const [securityWarning, setSecurityWarning] = useState(null);

  const allLogs = React.useMemo(() => [...liveLogs, ...runtimeLogs, ...deployLogs].sort((a, b) => (a.id > b.id ? 1 : -1)), [liveLogs, runtimeLogs, deployLogs]);

  useEffect(() => {
    templateSeededRef.current = false;
  }, [projectId]);

  // Track agent phase from WebSocket logs
  useEffect(() => {
    if (!liveLogs || liveLogs.length === 0) return;
    const lastLog = liveLogs[liveLogs.length - 1];
    if (lastLog?.event === 'phase_update') {
      setAgentPhase(lastLog.phase || 'generating');
      if (lastLog.phase === 'ready') {
        setTimeout(() => setAgentPhase('idle'), 3000);
      }
    } else if (lastLog?.event === 'single_shot_started' || lastLog?.event === 'fast_path_started') {
      setAgentPhase('generating');
    } else if (lastLog?.event === 'single_shot_completed' || lastLog?.event === 'file_progress') {
      if (agentPhase === 'generating') setAgentPhase('validation');
    } else if (lastLog?.event === 'security_scan_completed' && lastLog.blocked) {
      setSecurityWarning(lastLog.summary || {});
      toast.error('Security scan found HIGH/CRITICAL issues in this app — review before sharing or deploying it.');
    }
  }, [liveLogs]);

  // SECURITY-SCAN-POLL-BACKSTOP-FIX: `orchestrator_completed` is what
  // closes the WebSocket (useWebSocket.js's STOP_EVENTS) — and it's always
  // published BEFORE run_background_quality.delay(job.id) is even called
  // (core/orchestrator.py), so the WS listener above can never actually
  // receive `security_scan_completed` in real timing: the connection is
  // already closed by the time that background task gets to the scan
  // step. Poll the existing /security-findings endpoint for a short
  // window after generation finishes instead — it doesn't depend on the
  // socket staying open, and the finding is already persisted on the job
  // row by the time this starts polling for it.
  useEffect(() => {
    if (!liveLogs || liveLogs.length === 0) return;
    const lastLog = liveLogs[liveLogs.length - 1];
    if (lastLog?.event !== 'orchestrator_completed' || !currentJobId) return;

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 10;
    const POLL_MS = 8000;
    const jobIdAtStart = currentJobId;

    const poll = async () => {
      if (cancelled || attempts >= MAX_ATTEMPTS) return;
      attempts += 1;
      try {
        const data = await getSecurityFindings(jobIdAtStart);
        if (cancelled) return;
        if (data.security_blocked) {
          const summary = { LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0 };
          (data.findings || []).forEach((f) => {
            summary[f.severity] = (summary[f.severity] || 0) + 1;
          });
          setSecurityWarning(summary);
          toast.error('Security scan found HIGH/CRITICAL issues in this app — review before sharing or deploying it.');
          return; // found it — stop polling
        }
      } catch (e) {
        // best-effort backstop only — never surface this failure to the user
      }
      if (!cancelled) setTimeout(poll, POLL_MS);
    };
    const t = setTimeout(poll, POLL_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [liveLogs, currentJobId]);

  // Reset phase when generation stops
  useEffect(() => {
    if (!isGenerating && agentPhase !== 'idle') {
      setAgentPhase('ready');
      setTimeout(() => setAgentPhase('idle'), 3000);
    }
  }, [isGenerating]);

  const handleImported = (data) => {
    setShowImport(false);
    fetchProject();
    if (chatImportSuccessRef.current) chatImportSuccessRef.current(data);
  };

  // Called by ProjectChat when AI generation completes with new files
  const handleFilesUpdated = React.useCallback((generatedFiles) => {
    if (generatedFiles && typeof generatedFiles === 'object' && Object.keys(generatedFiles).length > 0) {
      history.push({ type: 'ai_generation', files: generatedFiles });
    }
    fetchProject();
  }, [history]);

  useEffect(() => {
    if (projectId) fetchProject();
  }, [projectId]);

  // HERO-SCREEN-HISTORY-FIX: "What will you build today?" must show ONLY
  // the very first time a project is opened — right after it's created,
  // before a single prompt has ever been sent — and NEVER again once the
  // project has any history at all, even a failed job that saved zero
  // files. The previous version of this check only looked at fileCount /
  // an active job, so clicking into an EXISTING project from the project
  // list whose one generation attempt had failed (0 files, no active job)
  // still showed the "new project" hero screen — confirmed live, and
  // wrong: that project isn't new, it already has an attempt on record.
  // "Has this project ever had a job at all" (regardless of status) is
  // the correct, permanent signal for "not first time" — checked once on
  // mount via the same /api/jobs?project_id= list already used to resume
  // an in-progress job below.
  const TERMINAL_JOB_STATUSES = ['done', 'failed', 'budget_exceeded', 'escalated', 'security_blocked', 'pm_spec_invalid', 'insufficient_credits', 'cancelled', 'needs_attention'];
  useEffect(() => {
    if (!projectId || initialJobId) return; // already have one — nothing to resume
    (async () => {
      try {
        const jobs = await listJobs(projectId);
        // THIS EFFECT USED TO THROW BEFORE IT GOT HERE. ONE-SCREEN-CREATE
        // removed the hero screen: `workspaceVisible` became a constant
        // `true` (its useState destructures no setter) and `heroState`
        // was deleted outright — but a `setWorkspaceVisible(true);
        // setHeroState('gone');` pair survived, right above this line.
        // "ReferenceError: setWorkspaceVisible is not defined" on every
        // mount was only the visible half: the throw aborted the effect
        // BEFORE the resume below, so reloading during a generation never
        // re-attached to the running job — no currentJobId, no
        // isGenerating, and a workspace that just sat there. Reported as
        // the UI not rendering on reload.
        const active = (jobs || []).find(j => !TERMINAL_JOB_STATUSES.includes(j.status));
        if (active) {
          setCurrentJobId(active.id);
          setIsGenerating(true);
        }

        // SECURITY-SCAN-ON-LOAD-FIX: the WS-based listener and the poll
        // backstop above only fire while THIS session watches a
        // generation finish — reopening an already-"done" project later
        // (a fresh page load, no live generation happening) never
        // triggers either one, even though job.security_blocked may
        // already be true from a PAST background scan. list_jobs already
        // returns security_blocked per job (newest first), so check the
        // latest job's flag directly instead of guessing.
        const latest = (jobs || [])[0];
        if (latest?.security_blocked) {
          try {
            const data = await getSecurityFindings(latest.id);
            const summary = { LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0 };
            (data.findings || []).forEach((f) => {
              summary[f.severity] = (summary[f.severity] || 0) + 1;
            });
            setSecurityWarning(summary);
          } catch (e) {
            // best-effort — never block the workspace from loading on this
          }
        }
      } catch (e) {
        console.error('Failed to check project job history', e);
      }
    })();
  }, [projectId]);

  const fetchProject = async () => {
    const startedAt = Date.now();
    const MIN_SKELETON_MS = 350;
    try {
      const res = await authFetch(`/api/projects/${projectId}`);
      if (res.ok) {
        const data = await res.json();
        setProject(data.project);
        let projectFiles = data.files || {};

        const shouldSeedTemplate =
          templateMeta?.type &&
          hasTemplateFiles(templateMeta.type) &&
          Object.keys(projectFiles).length === 0 &&
          !templateSeededRef.current;

        if (shouldSeedTemplate) {
          templateSeededRef.current = true;
          const seeded = await seedProjectTemplate(projectId, templateMeta.type);
          if (seeded && Object.keys(seeded).length > 0) {
            projectFiles = seeded;
            setActiveTab('preview');
            toast.success(`${templateMeta.name} template loaded`);
          }
        } else if (Object.keys(projectFiles).length > 0 && !initialJobId) {
          setActiveTab('preview');
        }

        setFiles(projectFiles);
      }
    } catch (e) {
      console.error('Failed to fetch project', e);
    } finally {
      const elapsed = Date.now() - startedAt;
      const wait = Math.max(0, MIN_SKELETON_MS - elapsed);
      setTimeout(() => setLoading(false), wait);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await authFetch(`/api/projects/${projectId}/download`);
      toast.success('Download started!');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project?.name || 'project'}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed: ' + e.message);
    }
  };

  const addDeployLog = React.useCallback((event, detail, type = '', agentType = 'DEPLOY') => {
    setDeployLogs(prev => [
      ...prev,
      {
        id: `dep-${++deployLogIdRef.current}`,
        time: new Date().toLocaleTimeString(),
        event,
        detail,
        type,
        phase: 'Deploying',
        agentType,
      },
    ]);
  }, []);

  const [publishing, setPublishing] = useState(false);
  const [deployResult, setDeployResult] = useState(null); // { url, platform }
  // DEPLOY-AUTO-ROUTE-FIX: this used to default to (and effectively always
  // send) 'vercel' as a concrete platform_preference — since the backend's
  // own rule is "preference always wins when present" (services/
  // deployment_service.get_platform_for_stack), that meant its stack-based
  // auto-routing (dynamic FE+BE+DB app -> Railway, static frontend ->
  // Vercel) never actually ran; every deploy forced Vercel regardless of
  // the project, which is exactly how a dynamic app's backend ended up
  // silently stripped on every publish. 'auto' is now the default: no
  // platform_preference is sent, so the backend decides from the real
  // project structure. The dropdown still lets someone force a specific
  // platform when they want to.
  const [deployPlatform, setDeployPlatform] = useState('auto');
  const handlePublish = async () => {
    setPublishing(true);
    setActiveTab('log');
    setDeployLogs([]);
    addDeployLog('deployment_log', deployPlatform === 'auto'
      ? 'Detecting the right platform for this project...'
      : `Initializing deployment to ${deployPlatform}...`);
    addDeployLog('deployment_log', `Preparing ${Object.keys(files).length} files for upload...`);
    try {
      addDeployLog('install_log', 'Installing dependencies...', '', 'INSTALL');
      addDeployLog('build_log', 'Running build...', '', 'BUILD');
      const res = await authFetch(`/api/deploy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          files,
          project_name: project?.name || 'ai-app',
          ...(deployPlatform !== 'auto' ? { platform_preference: deployPlatform } : {}),
          project_id: projectId,
        }),
      });
      const data = await res.json();
      if (data.success && data.url) {
        addDeployLog('build_log', 'Build completed successfully', 'success', 'BUILD');
        addDeployLog('deployment_success', `Deployed to ${data.url}`, 'success');
        setDeployResult({ url: data.url, platform: data.platform || deployPlatform, projectId });
      } else {
        const errMsg = data.detail || data.error || 'Publish failed — check deploy token in .env';
        addDeployLog('deployment_failed', errMsg, 'error');
        toast.error(errMsg);
      }
    } catch (e) {
      addDeployLog('deployment_failed', `Deployment error: ${e.message}`, 'error');
      toast.error('Publish failed: ' + e.message);
    } finally {
      setPublishing(false);
    }
  };

  const fileCount = Object.keys(files).length;
  // The reveal-the-workspace effect that used to live here is gone with
  // the hero screen: `workspaceVisible` is now a constant `true`, so its
  // `!workspaceVisibleRef.current` guard could never be true and the
  // body — which called the same two deleted setters — was unreachable.
  // Left in place it was a second copy of the bug above, waiting for
  // someone to make the guard reachable again.

  // Immediately transition hero → workspace when user clicks Generate.
  // ONE-SCREEN-CREATE: a project created WITH a prompt starts generating
  // the moment its workspace mounts, so "Create & Open Workspace" lands on
  // code + preview already building — the step the hero screen used to be.
  //
  // It waits for ProjectChat to register its imperative handle (the same
  // sendFromHero the hero screen called) and fires exactly once, guarded by
  // a ref so a re-render or a late `loading` flip cannot start a second
  // generation.
  const initialConsumedRef = React.useRef(false);
  React.useEffect(() => {
    if (loading || initialConsumedRef.current) return;
    if (!initialPrompt && !initialAction) return;

    let cancelled = false;
    let attempts = 0;
    const tick = () => {
      if (cancelled || initialConsumedRef.current) return;
      const chat = chatRef.current;
      const ready = chat && (chat.sendFromHero || chat.openReferenceModal);
      if (!ready) {
        // ProjectChat mounts a tick or two after this effect runs.
        if (attempts++ < 40) { timer = setTimeout(tick, 100); }
        return;
      }
      initialConsumedRef.current = true;

      if (initialAction === 'import') {
        setShowImport(true);
      } else if (initialAction === 'reference') {
        // ProjectChat owns the reference modal and exposes it on its
        // imperative handle, the same one the hero screen used.
        chat && chat.openReferenceModal && chat.openReferenceModal();
      } else if (initialPrompt && chat.sendFromHero) {
        setIsGenerating(true);
        chat.sendFromHero(initialPrompt.text, initialPrompt.heroVideoAttachment || null);
      }
      onInitialConsumed && onInitialConsumed();
    };
    let timer = setTimeout(tick, 60);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [loading, initialPrompt, initialAction, onInitialConsumed]);

  if (loading) return <WorkspaceSkeleton />;

  const previewSourceFiles = previewFiles || files;
  // NEVER PREVIEW A BUILD THAT IS STILL BEING WRITTEN.
  // This used to be "are there any files?" alone, so as soon as a single
  // file existed — including a half-finished one from a generation still
  // in progress — the Preview tab mounted a real Vite dev server against
  // it. Confirmed live on project 9505b8ee: the user was shown Vite's raw
  // error overlay ("Unexpected token (34:60)" with a @babel/parser stack
  // trace) for a DashboardPage that the pipeline went on to repair
  // moments later. The loader stays up until the job reaches a terminal
  // status; see ProjectChat's onGeneratingChange, which now fires only
  // after the finished files have been saved and reloaded.
  const canPreview = Object.keys(previewSourceFiles).length > 0 && !isGenerating;
  const isViewer = collab.myRole === 'viewer';

  return (
    <div style={s.container}>
      {/* Collab overlays */}
      <ReconnectBanner reconnectAttempt={collab.reconnectAttempt} connected={collab.connected} />
      {showInvite && (
        <InviteDialog
          projectId={projectId}
          myRole={collab.myRole || 'owner'}
          onClose={() => setShowInvite(false)}
        />
      )}
      {showActivity && (
        <ActivityFeed
          projectId={projectId}
          liveEvents={liveActivityEvents}
          onClose={() => setShowActivity(false)}
        />
      )}
      {collab.aiDiff && (
        <AIDiffReview
          diff={collab.aiDiff}
          projectId={projectId}
          onApplied={() => { collab.clearAiDiff(); fetchProject(); }}
          onDismiss={collab.clearAiDiff}
        />
      )}
      {deployResult && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:9998, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={() => setDeployResult(null)}>
          <div style={{ background:'var(--bg-card)', borderRadius:16, padding:'32px 36px', maxWidth:480, width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,0.4)', border:'1px solid var(--border)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 style={{ fontSize:18, fontWeight:700, color:'var(--text-primary)', margin:0 }}>🚀 Deployment Successful!</h3>
              <button onClick={() => setDeployResult(null)} style={{ background:'none', border:'none', color:'var(--text-muted)', fontSize:20, cursor:'pointer', padding:'4px 8px' }}>✕</button>
            </div>
            <p style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:16 }}>Your app is live on {deployResult.platform}:</p>
            <div style={{ display:'flex', gap:8, alignItems:'center', background:'var(--bg-input)', borderRadius:8, padding:'10px 14px', border:'1px solid var(--border)' }}>
              <input readOnly value={deployResult.url} style={{ flex:1, background:'none', border:'none', color:'var(--text-primary)', fontSize:14, outline:'none', fontFamily:'monospace' }} />
              <button onClick={() => { navigator.clipboard.writeText(deployResult.url); toast.success('URL copied!'); }} style={{ padding:'6px 12px', background:'var(--accent)', color:'#fff', border:'none', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>Copy</button>
            </div>
            <div style={{ display:'flex', gap:10, marginTop:20 }}>
              <button onClick={() => window.open(deployResult.url, '_blank')} style={{ flex:1, padding:'10px 16px', background:'var(--accent)', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>🌐 Open Live Site</button>
              <button onClick={() => setDeployResult(null)} style={{ padding:'10px 16px', background:'none', color:'var(--text-secondary)', border:'1px solid var(--border)', borderRadius:8, fontSize:13, cursor:'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      {showPush && (
        <GitPushModal
          projectId={projectId}
          projectName={project?.name || 'project'}
          onClose={() => setShowPush(false)}
        />
      )}
      {showShare && (
        <ShareModal
          projectId={projectId}
          onClose={() => setShowShare(false)}
        />
      )}

      {/* Full-screen Import Modal */}
      {showImport && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <GitHubImport
              projectId={projectId}
              onImported={handleImported}
              onCancel={() => setShowImport(false)}
            />
          </div>
        </div>
      )}

      {/* Top Bar */}
      <div style={s.topBar}>
        <div style={s.topLeft}>
          <button onClick={onBack} style={s.backBtn}>← Back</button>
          <h2 style={s.projectName}>{project?.name || 'Project'}</h2>
          <span style={s.badge}>{project?.framework}</span>
          <span style={s.filesBadge}>{fileCount} files</span>
          <AgentProgressBar phase={agentPhase} isVisible={agentPhase !== 'idle'} />
          {isViewer && (
            <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', padding: '2px 8px', borderRadius: 6 }}>👁 Viewer</span>
          )}
          <CollabBar
            members={collab.members}
            myColor={collab.myColor}
            connected={collab.connected}
            isViewer={isViewer}
            onOpenInvite={() => setShowInvite(true)}
            onOpenActivity={() => setShowActivity(true)}
          />
        </div>
        <div style={s.topRight}>
          <button
            type="button"
            onClick={toggle}
            style={{ ...s.actionBtn, fontSize: 16, padding: '4px 10px' }}
            title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label="Toggle theme"
          >
            {mode === 'dark' ? '☀️' : '🌙'}
          </button>
          {onOpenSettings && (
            <button type="button" onClick={onOpenSettings} style={s.actionBtn} title="Settings"><img src={settingsIcon} alt="Settings" style={s.actionIcon} /> Settings</button>
          )}
          <button onClick={handleDownload} style={s.actionBtn}><img src={downloadIcon} alt="Download" style={s.actionIcon} /> Download ZIP</button>
          {!isViewer && (
            <>
              <button onClick={() => setShowShare(true)} style={s.actionBtn}><img src={shareIcon} alt="Share" style={s.actionIcon} /> Share</button>
              <button onClick={() => setShowPush(true)} style={{ ...s.actionBtn, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none' }}><img src={gitPushIcon} alt="Push" style={{ ...s.actionIcon, filter: 'brightness(0) invert(1)' }} /> Push to GitHub / Bitbucket</button>
              <select
                value={deployPlatform}
                onChange={e => setDeployPlatform(e.target.value)}
                title="Auto picks Railway for a dynamic app (real backend + database) and Vercel for a static frontend. Override only if you need a specific platform."
                style={{ padding:'6px 10px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:12, fontWeight:600, cursor:'pointer' }}
              >
                <option value="auto">Auto (recommended)</option>
                <option value="vercel">Vercel</option>
                <option value="netlify">Netlify</option>
                <option value="railway">Railway</option>
              </select>
              <button data-tour="deploy-btn" onClick={handlePublish} disabled={publishing} style={{ ...s.actionBtn, ...s.publishBtn }}>
                {publishing ? '⏳ Publishing...' : '🚀 Publish'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Transition wrapper — both hero and workspace are always mounted ── */}
      <style>{`
        @keyframes ws-hero-out {
          from { opacity:1; transform:scale(1) translateY(0); }
          to   { opacity:0; transform:scale(0.97) translateY(-12px); }
        }
        @keyframes ws-panel-in {
          from { opacity:0; transform:translateY(10px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes ws-left-in {
          from { opacity:0; transform:translateX(-18px); }
          to   { opacity:1; transform:translateX(0); }
        }
        @keyframes ws-right-in {
          from { opacity:0; transform:translateX(18px); }
          to   { opacity:1; transform:translateX(0); }
        }
        .ws-hero-exit {
          animation: ws-hero-out 0.28s cubic-bezier(0.4,0,0.2,1) forwards;
          pointer-events: none;
        }
        .ws-workspace-enter .ws-left-panel-anim {
          animation: ws-left-in 0.35s cubic-bezier(0.22,1,0.36,1) 0.05s both;
        }
        .ws-workspace-enter .ws-right-panel-anim {
          animation: ws-right-in 0.35s cubic-bezier(0.22,1,0.36,1) 0.1s both;
        }
      `}</style>

      {/* Main Content */}
      <div style={{ ...s.main, display: workspaceVisible ? 'flex' : 'none' }}
           className={workspaceVisible ? 'ws-workspace-enter' : ''}>
        {/* Left: Preview/Code */}
        <div style={s.leftPanel} className="ws-left-panel-anim">
          <style>{`
            .ws-panel-tabs {
              display: flex;
              gap: 4px;
              padding: 8px 12px 0;
            }
            .ws-panel-tab {
              appearance: none;
              -webkit-appearance: none;
              display: inline-flex;
              align-items: center;
              gap: 6px;
              padding: 6px 14px;
              margin: 0;
              border-width: 0 0 2px 0;
              border-style: solid;
              border-color: transparent;
              border-radius: 6px 6px 0 0;
              background: transparent;
              color: var(--text-muted);
              font-size: 12px;
              font-weight: 600;
              font-family: inherit;
              cursor: pointer;
              outline: none;
              box-shadow: none;
            }
            .ws-panel-tab:hover:not(.ws-panel-tab--active) {
              color: var(--text-secondary);
              background: rgba(99, 102, 241, 0.04);
            }
            .ws-panel-tab--active {
              color: var(--text-primary);
              border-bottom-width: 2px;
              border-bottom-style: solid;
              border-bottom-color: #6366f1;
              background: rgba(99, 102, 241, 0.06);
            }
            [data-theme="light"] .ws-panel-tab--active {
              border-bottom-color: #4f46e5;
            }
            .ws-panel-tab:focus,
            .ws-panel-tab:focus-visible {
              outline: none;
              box-shadow: none;
            }
            .ws-panel-tab-icon {
              display: inline-block;
              width: 16px;
              height: 16px;
              flex-shrink: 0;
              background-color: var(--text-muted);
              -webkit-mask-repeat: no-repeat;
              mask-repeat: no-repeat;
              -webkit-mask-position: center;
              mask-position: center;
              -webkit-mask-size: contain;
              mask-size: contain;
            }
            .ws-panel-tab:hover:not(.ws-panel-tab--active) .ws-panel-tab-icon {
              background-color: var(--text-secondary);
            }
            .ws-panel-tab--active .ws-panel-tab-icon {
              background-color: var(--text-primary);
            }
          `}</style>
          <div className="ws-panel-tabs">
            {PANEL_TABS.map((tab) => {
              const isDisabled = tab.id === 'domains';
              return (
                <button
                  key={tab.id}
                  type="button"
                  data-tour={`tab-${tab.id}`}
                  onClick={() => !isDisabled && setActiveTab(tab.id)}
                  className={`ws-panel-tab${activeTab === tab.id ? ' ws-panel-tab--active' : ''}${isDisabled ? ' ws-panel-tab--disabled' : ''}`}
                  disabled={isDisabled}
                  title={isDisabled ? 'Coming soon' : undefined}
                  style={isDisabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                >
                  {tab.icon ? (
                    <span
                      className="ws-panel-tab-icon"
                      style={{
                        WebkitMaskImage: `url(${tab.icon})`,
                        maskImage: `url(${tab.icon})`,
                      }}
                      aria-hidden
                    />
                  ) : (
                    <span style={{ fontSize: 14 }}>🌐</span>
                  )}
                  <span>{tab.label}</span>
                  {tab.id === 'log' && isGenerating && (
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: '#ef4444',
                        display: 'inline-block',
                        animation: 'pulse 1s infinite',
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
          {securityWarning && (
            <div
              role="alert"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, padding: '10px 16px', background: '#fef2f2', border: '1px solid #fecaca',
                borderLeft: '4px solid #dc2626', color: '#991b1b', fontSize: 13, fontWeight: 500,
              }}
            >
              <span>
                ⚠ Security scan found {securityWarning.CRITICAL || 0} critical and {securityWarning.HIGH || 0} high-severity issue(s) in this app. Review findings before sharing or deploying.
              </span>
              <button
                type="button"
                onClick={() => setSecurityWarning(null)}
                style={{ background: 'none', border: 'none', color: '#991b1b', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          )}
          <div style={s.tabContent}>
            {activeTab === 'preview' && canPreview && (
              <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {project?.project_type === 'mobile' ? (
                <MobilePreview files={previewSourceFiles} projectName={project?.name} projectId={projectId} />
              ) : (
                <TemplateFilePreview
                  files={previewSourceFiles}
                  templateType={templateMeta?.type}
                  projectId={projectId}
                  jobId={currentJobId}
                  onFilesUpdated={fetchProject}
                  onRuntimeError={handleRuntimeError}
                  onElementEditRequest={isViewer ? undefined : (prompt) => chatRef.current?.sendFromHero(prompt)}
                />
              )}
              </div>
            )}
            {activeTab === 'preview' && !canPreview && (
              <div style={s.emptyPreview}>
                {isGenerating ? (
                  <GeneratingProgress logs={allLogs} />
                ) : (
                  <EmptyPreviewState />
                )}
              </div>
            )}
            {activeTab === 'code' && (
              <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <CodeEditor files={previewFiles || files} jobId={projectId} onFilesChanged={fetchProject} history={history} isViewer={isViewer} />
              </div>
            )}
            {activeTab === 'history' && (
              <div style={s.historyWrap}>
                <IterationHistory
                  projectId={projectId}
                  onPreviewFiles={(jobFiles) => { setPreviewFiles(jobFiles); setActiveTab('preview'); }}
                  onRestored={() => { history.clearHistory(); setPreviewFiles(null); fetchProject(); }}
                />
              </div>
            )}
            {/* Keep mounted so WebSocket connects as soon as jobId exists (not only when tab is visible) */}
            <div
              style={{
                display: activeTab === 'log' ? 'flex' : 'none',
                flex: 1,
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <LogTab
                projectId={projectId}
                jobId={currentJobId}
                isGenerating={isGenerating}
                logs={allLogs}
                isConnected={logsConnected}
                reconnectAttempt={logsReconnectAttempt}
                reconnectCountdown={logsReconnectCountdown}
                fatalError={logsFatalError}
              />
            </div>
            {activeTab === 'domains' && (
              <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                <DomainsTab
                  jobId={projectId}
                  deployUrl={deployResult?.url || project?.deploy_url}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right: Chat */}
        <div style={s.rightPanel} className="ws-right-panel ws-right-panel-anim">
          <ProjectChat
            projectId={projectId}
            initialJobId={currentJobId}
            templateMeta={templateMeta}
            hasProjectFiles={fileCount > 0}
            onFilesUpdated={handleFilesUpdated}
            onGeneratingChange={(val, jobId) => { setIsGenerating(val); if (jobId) setCurrentJobId(jobId); }}
            onOpenImport={() => setShowImport(true)}
            ref={chatRef}
            onRegisterImportSuccess={(fn) => { chatImportSuccessRef.current = fn; }}
            hasFiles={fileCount > 0}
            isViewer={isViewer}
          />
        </div>
      </div>
    </div>
  );
}

// ── Empty Preview State ──────────────────────────────────────────────────────
function EmptyPreviewState() {
  return (
    <div style={{ display:'flex', flex:1, alignItems:'center', justifyContent:'center', background:'var(--bg-primary)' }}>
      <p style={{ fontSize:13, color:'var(--text-muted)' }}>No files yet. Use the <strong style={{ color:'var(--accent-light)' }}>Project Chat →</strong> to start building.</p>
    </div>
  );
}

/** Human phase heading shown above the file list — reflects what the
 * backend is actually doing right now instead of a static label the whole
 * time generation runs. */
function phaseHeading(lastStep) {
  if (!lastStep) return 'Analyzing requirements...';
  const tagMatch = /^\[(\w+)\]/.exec(lastStep.file || '');
  const tag = tagMatch ? tagMatch[1] : null;
  if (lastStep.file?.startsWith('Complete:')) return 'Finishing up...';
  if (tag === 'scaffold') return 'Scaffolding project structure...';
  if (tag === 'retry') return 'Auto-fixing weak pages...';
  if (tag === 'pages') return 'Wrapping up pages...';
  if (tag === 'backend') return 'Generating backend...';
  return 'Generating pages...';
}

// Maps real backend agent/phase values → which stage index is active
function deriveStageIndex(logs, steps) {
  if (!logs || logs.length === 0) return 0;
  if (steps.length > 0) {
    const lastStep = steps[steps.length - 1];
    if (lastStep?.file?.startsWith('Complete:')) return 5;
    const tag = /^\[(\w+)\]/.exec(lastStep?.file || '')?.[1];
    if (tag === 'retry') return 4;
    if (tag === 'scaffold') return 2;
    if (tag === 'backend') return 3;
    return 3;
  }
  for (let i = logs.length - 1; i >= 0; i--) {
    const l = logs[i];
    const agent = (l.agentType || '').toUpperCase();
    const phase = (l.phase || '').toLowerCase();
    if (agent === 'PM') return 0;
    if (agent === 'ARCHITECT' || phase === 'planning' || phase === 'architecting') return 1;
    if (agent === 'DEVELOPER' || phase === 'executing') return 3;
    if (agent === 'VALIDATOR' || agent === 'QA' || phase === 'validating') return 4;
    if (agent === 'DEVOPS' || phase === 'deploying') return 5;
  }
  return 0;
}

const GP_STAGES = [
  { label: 'Understanding your idea',       icon: '◎' },
  { label: 'Planning application structure', icon: '◈' },
  { label: 'Creating interface',             icon: '◧' },
  { label: 'Generating code',                icon: '◉' },
  { label: 'Configuring project',            icon: '◫' },
  { label: 'Preparing live preview',         icon: '◍' },
];

function GeneratingProgress({ logs }) {
  const [steps, setSteps] = React.useState([]);
  const [percent, setPercent] = React.useState(0);
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    if (!logs || logs.length === 0) return;
    const parsed = logs
      .filter(l => l.event === 'file_progress' && l.fileProgress)
      .map(l => l.fileProgress);
    if (parsed.length > 0) {
      setSteps(prev => {
        const seen = new Set(prev.map(s => s.file));
        const fresh = parsed.filter(s => !seen.has(s.file));
        return [...prev, ...fresh];
      });
      const lastWithPercent = [...parsed].reverse().find(s => s.percent != null);
      if (lastWithPercent) setPercent(lastWithPercent.percent);
    }
  }, [logs]);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [steps]);

  const stageIndex = deriveStageIndex(logs, steps);
  const realFileCount = steps.filter(s => !/^\[/.test(s.file) && !s.file?.startsWith('Complete:')).length;
  const displayPercent = percent > 0 ? percent : Math.min(stageIndex * 17, 85);

  return (
    <div style={gp.root}>
      <style>{`
        @keyframes gp-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes gp-spin-rev { from{transform:rotate(0deg)} to{transform:rotate(-360deg)} }
        @keyframes gp-float1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(22px,-16px)} }
        @keyframes gp-float2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-16px,22px)} }
        @keyframes gp-scan { 0%{top:-2px} 100%{top:100%} }
        @keyframes gp-fadein { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes gp-bar-shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes gp-stage-pulse { 0%,100%{box-shadow:0 0 0 0 rgba(99,102,241,0.5)} 50%{box-shadow:0 0 0 5px rgba(99,102,241,0)} }
        @keyframes gp-active-blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
        .gp-file-row { animation: gp-fadein 0.22s ease both; }
      `}</style>

      {/* Background */}
      <div style={gp.bg} aria-hidden>
        <div style={gp.orb1} />
        <div style={gp.orb2} />
        <div style={gp.grid} />
        <div style={gp.scanLine} />
      </div>

      {/* Center content */}
      <div style={gp.center}>

        {/* Spinner */}
        <div style={gp.spinnerWrap}>
          <div style={gp.ringOuter} />
          <div style={gp.ringInner} />
          <div style={gp.spinnerCore}>
            <span style={{ fontSize: 20, lineHeight: 1 }}>⚡</span>
          </div>
          <div style={gp.orbitTrack}>
            <div style={gp.orbitDot} />
          </div>
        </div>

        {/* Title */}
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <p style={gp.title}>Building your application</p>
          <p style={gp.subtitle}>
            {realFileCount === 0
              ? GP_STAGES[stageIndex]?.label + '…'
              : `${realFileCount} file${realFileCount === 1 ? '' : 's'} generated`}
          </p>
        </div>

        {/* Gradient progress bar */}
        <div style={gp.barTrack}>
          <div style={{ ...gp.barFill, width: `${displayPercent}%` }}>
            <div style={gp.barShimmer} />
          </div>
        </div>
        {percent > 0 && (
          <div style={gp.barPct}>{percent}%</div>
        )}

        {/* Stage pipeline */}
        <div style={gp.stages}>
          {GP_STAGES.map((stage, i) => {
            const done = i < stageIndex;
            const active = i === stageIndex;
            return (
              <div key={stage.label} style={gp.stageRow}>
                {i > 0 && (
                  <div style={{ ...gp.connector, background: done || active ? 'rgba(99,102,241,0.45)' : 'rgba(99,102,241,0.1)' }} />
                )}
                <div style={{
                  ...gp.stageDot,
                  ...(done ? gp.stageDotDone : {}),
                  ...(active ? gp.stageDotActive : {}),
                }}>
                  {done
                    ? <span style={{ fontSize: 9, color: '#10b981', fontWeight: 700 }}>✓</span>
                    : <span style={{ fontSize: 10, color: active ? '#a5b4fc' : 'rgba(99,102,241,0.3)', lineHeight: 1 }}>{stage.icon}</span>
                  }
                </div>
                <span style={{
                  ...gp.stageLabel,
                  color: done ? 'var(--text-secondary)' : active ? 'var(--text-primary)' : 'var(--text-muted)',
                  fontWeight: active ? 600 : 400,
                }}>
                  {stage.label}
                  {active && <span style={gp.activePulse} />}
                </span>
              </div>
            );
          })}
        </div>

        {/* FILE-LIST-TO-CHAT-FIX: the per-file/scaffold live stream used to
            render here (in the center preview panel) — moved to Project
            Chat (see ProjectChat.js's `file_progress` pipeline handling)
            per explicit request, so the center panel only shows the
            high-level spinner/progress-bar/stage-pipeline above and the
            granular file names/scaffold markers show up in chat instead. */}
      </div>
    </div>
  );
}

const gp = {
  // Full-height root — fills the emptyPreview container
  root: {
    position: 'relative', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    height: '100%', width: '100%', overflow: 'hidden',
    background: 'var(--bg-primary)',
  },
  // Background layer
  bg: { position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' },
  orb1: {
    position: 'absolute', top: '8%', left: '5%', width: 380, height: 380,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(99,102,241,0.09) 0%, transparent 68%)',
    animation: 'gp-float1 16s ease-in-out infinite',
  },
  orb2: {
    position: 'absolute', bottom: '10%', right: '4%', width: 300, height: 300,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 68%)',
    animation: 'gp-float2 20s ease-in-out infinite',
  },
  grid: {
    position: 'absolute', inset: 0,
    backgroundImage: 'linear-gradient(rgba(99,102,241,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.03) 1px, transparent 1px)',
    backgroundSize: '52px 52px',
  },
  scanLine: {
    position: 'absolute', left: 0, right: 0, height: 2,
    background: 'linear-gradient(90deg, transparent 0%, rgba(99,102,241,0.18) 30%, rgba(139,92,246,0.22) 50%, rgba(99,102,241,0.18) 70%, transparent 100%)',
    animation: 'gp-scan 4s linear infinite',
    top: 0,
  },
  // Center content column
  center: {
    position: 'relative', zIndex: 1,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 0, width: '100%', maxWidth: 400, padding: '0 24px',
    animation: 'gp-fadein 0.4s ease',
  },
  // Spinner
  spinnerWrap: { position: 'relative', width: 72, height: 72, flexShrink: 0 },
  ringOuter: {
    position: 'absolute', inset: 0, borderRadius: '50%',
    border: '2px solid transparent',
    borderTopColor: '#6366f1', borderRightColor: 'rgba(99,102,241,0.2)',
    animation: 'gp-spin 1.1s linear infinite',
  },
  ringInner: {
    position: 'absolute', inset: 10, borderRadius: '50%',
    border: '1.5px solid transparent',
    borderBottomColor: '#8b5cf6', borderLeftColor: 'rgba(139,92,246,0.2)',
    animation: 'gp-spin-rev 1.7s linear infinite',
  },
  spinnerCore: {
    position: 'absolute', inset: 18, borderRadius: '50%',
    background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 0 18px rgba(99,102,241,0.4)',
  },
  orbitTrack: {
    position: 'absolute', inset: -4, borderRadius: '50%',
    animation: 'gp-spin 2.4s linear infinite',
  },
  orbitDot: {
    position: 'absolute', top: 2, left: '50%', marginLeft: -3,
    width: 6, height: 6, borderRadius: '50%',
    background: '#a5b4fc', boxShadow: '0 0 6px rgba(165,180,252,0.8)',
  },
  // Text
  title: {
    fontSize: 16, fontWeight: 700, color: 'var(--text-primary)',
    margin: '0 0 5px', letterSpacing: '-0.01em', textAlign: 'center',
  },
  subtitle: {
    fontSize: 12, color: 'var(--text-muted)', margin: 0,
    textAlign: 'center', minHeight: 18,
  },
  // Progress bar
  barTrack: {
    width: '100%', height: 3, borderRadius: 3,
    background: 'rgba(99,102,241,0.12)', overflow: 'hidden',
    marginTop: 20, position: 'relative',
  },
  barFill: {
    height: '100%', borderRadius: 3, position: 'relative', overflow: 'hidden',
    background: 'linear-gradient(90deg, #4f46e5, #7c3aed, #10b981)',
    transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
    minWidth: 6,
  },
  barShimmer: {
    position: 'absolute', inset: 0,
    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)',
    backgroundSize: '200% 100%',
    animation: 'gp-bar-shimmer 1.8s linear infinite',
  },
  barPct: {
    fontSize: 10, color: 'var(--accent-light)', fontWeight: 600,
    alignSelf: 'flex-end', marginTop: 4,
  },
  // Stage pipeline
  stages: {
    display: 'flex', flexDirection: 'column', gap: 0,
    width: '100%', marginTop: 24,
  },
  stageRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    position: 'relative', paddingLeft: 2,
  },
  connector: {
    position: 'absolute', left: 13, top: -8,
    width: 1, height: 8,
    transition: 'background 0.4s ease',
  },
  stageDot: {
    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '1.5px solid rgba(99,102,241,0.2)',
    background: 'var(--bg-card)',
    transition: 'all 0.35s ease',
  },
  stageDotDone: {
    border: '1.5px solid rgba(16,185,129,0.5)',
    background: 'rgba(16,185,129,0.08)',
  },
  stageDotActive: {
    border: '1.5px solid rgba(99,102,241,0.7)',
    background: 'rgba(99,102,241,0.1)',
    animation: 'gp-stage-pulse 2s ease-in-out infinite',
  },
  stageLabel: {
    fontSize: 12, transition: 'color 0.35s ease',
    display: 'flex', alignItems: 'center', gap: 6,
    paddingTop: 6, paddingBottom: 6,
  },
  activePulse: {
    display: 'inline-block', width: 5, height: 5, borderRadius: '50%',
    background: '#6366f1', marginLeft: 2,
    animation: 'gp-active-blink 1.2s ease-in-out infinite',
  },
  // File stream panel
  filePanel: {
    width: '100%', marginTop: 20,
    background: 'rgba(99,102,241,0.04)',
    border: '1px solid rgba(99,102,241,0.1)',
    borderRadius: 10, overflow: 'hidden', position: 'relative',
    maxHeight: 160,
  },
  filePanelFadeTop: {
    position: 'absolute', top: 0, left: 0, right: 0, height: 20,
    background: 'linear-gradient(to bottom, var(--bg-primary), transparent)',
    zIndex: 1, pointerEvents: 'none',
  },
  fileScroll: {
    display: 'flex', flexDirection: 'column', gap: 1,
    overflowY: 'auto', maxHeight: 160, padding: '8px 0 6px',
  },
  fileRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '3px 12px', borderRadius: 0,
  },
  fileRowActive: { background: 'rgba(99,102,241,0.07)' },
  dotPulse: {
    width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
    background: '#6366f1', display: 'inline-block',
    animation: 'gp-active-blink 0.9s ease-in-out infinite',
  },
  checkDot: {
    fontSize: 9, color: '#10b981', flexShrink: 0,
    width: 5, textAlign: 'center',
  },
  tagBadge: {
    fontSize: 9, fontWeight: 700, color: '#a5b4fc',
    background: 'rgba(99,102,241,0.15)', padding: '1px 5px',
    borderRadius: 3, flexShrink: 0, letterSpacing: '0.03em',
  },
  fileText: {
    fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  filePath: {
    fontSize: 11, fontFamily: '"JetBrains Mono","Fira Code",monospace',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
};

const s = {
  container: { display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: 'var(--bg-primary)' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--text-secondary)' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' },
  topLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  topRight: { display: 'flex', gap: 8 },
  backBtn: { padding: '6px 12px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer' },
  projectName: { fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' },
  badge: { fontSize: 10, fontWeight: 600, color: 'var(--accent-light)', background: 'rgba(99,102,241,0.12)', padding: '2px 8px', borderRadius: 6, textTransform: 'uppercase' },
  filesBadge: { fontSize: 11, color: 'var(--text-muted)' },
  actionBtn: { padding: '6px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 },
  actionIcon: { width: 16, height: 16, objectFit: 'contain', filter: 'var(--icon-filter)' },
  publishBtn: { background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', color: '#fff' },
  main: { display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 },
  leftPanel: { flex: 1, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border)' },
  tabContent: {
    flex: 1,
    overflow: 'hidden',
    position: 'relative',
    minHeight: 0,
    borderTop: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
  },
  emptyPreview: { display: 'flex', flex: 1, minHeight: 0 },
  historyWrap: { display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 },
  rightPanel: { width: '30%', minWidth: 320, maxWidth: 440, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'width 0.3s ease' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { width: '100%', maxWidth: 624, minHeight: 380, margin: '0 16px', display: 'flex', flexDirection: 'column' },
};
